/**
 * Output distribution histogram with spec limits.
 *
 * One series, so one colour (categorical slot 1) — bar height already encodes
 * magnitude and a value-ramp would burn the free channel re-encoding it. Bars
 * falling outside a spec limit take the `critical` status colour, which is a
 * genuine status meaning rather than a second series. Colour never carries that
 * alone: the limit rules are drawn and labelled, and the out-of-spec count is
 * stated in text beneath.
 */

import { useId, useMemo, useState } from 'react';
import type { OutputResult } from '../../engine/montecarlo.ts';
import { eng, engAxis, count, niceTicks, percent } from '../format.ts';

interface Props {
  result: OutputResult;
  height?: number;
}

const PAD = { top: 16, right: 16, bottom: 34, left: 52 };

export function Histogram({ result, height = 260 }: Props) {
  const titleId = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);

  const { histogram, lsl, usl, unit, stats } = result;

  const view = useMemo(() => {
    const counts = histogram.counts;
    const n = counts.length;

    // Trim empty margins so a wide auto-range does not squash the distribution
    // into a few pixels in the middle of the plot.
    let first = 0;
    let last = n - 1;
    while (first < n && counts[first] === 0) first++;
    while (last > first && counts[last] === 0) last--;
    if (first > last) return null;

    const pad = Math.max(1, Math.round((last - first) * 0.02));
    first = Math.max(0, first - pad);
    last = Math.min(n - 1, last + pad);

    const xLo = histogram.lo + first * histogram.binWidth;
    const xHi = histogram.lo + (last + 1) * histogram.binWidth;
    let peak = 0;
    for (let i = first; i <= last; i++) peak = Math.max(peak, counts[i]);

    return { first, last, xLo, xHi, peak };
  }, [histogram]);

  if (!view) return <p className="muted">No samples to plot.</p>;

  const width = 720;
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  // Include the spec limits in the visible domain so a limit is never off-plot.
  const domainLo = Math.min(view.xLo, lsl ?? Infinity);
  const domainHi = Math.max(view.xHi, usl ?? -Infinity);

  const x = (v: number) => PAD.left + ((v - domainLo) / (domainHi - domainLo)) * plotW;
  const y = (c: number) => PAD.top + plotH - (c / view.peak) * plotH;

  const bars: { i: number; x0: number; x1: number; c: number; bad: boolean }[] = [];
  for (let i = view.first; i <= view.last; i++) {
    const v0 = histogram.lo + i * histogram.binWidth;
    const v1 = v0 + histogram.binWidth;
    const c = histogram.counts[i];
    const mid = (v0 + v1) / 2;
    bars.push({
      i,
      x0: x(v0),
      x1: x(v1),
      c,
      bad: (lsl !== undefined && mid < lsl) || (usl !== undefined && mid > usl),
    });
  }

  const ticks = niceTicks(domainLo, domainHi, 6);
  const tickLabel = engAxis(ticks, unit, 3);
  const outOfSpec = stats.belowLsl + stats.aboveUsl;
  const hovered = hover !== null ? bars.find((b) => b.i === hover) : null;

  return (
    <figure className="chart">
      <figcaption id={titleId} className="chart-title">
        {result.id} distribution
        <button
          type="button"
          className="link-btn"
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
        >
          {showTable ? 'Hide data' : 'Show data'}
        </button>
      </figcaption>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart-svg"
        role="img"
        aria-labelledby={titleId}
        onMouseLeave={() => setHover(null)}
      >
        {/* Recessive hairline grid */}
        {niceTicks(0, view.peak, 4).map((c) => (
          <line
            key={c}
            x1={PAD.left}
            x2={width - PAD.right}
            y1={y(c)}
            y2={y(c)}
            stroke="var(--gridline)"
            strokeWidth={1}
          />
        ))}

        {/* Out-of-spec regions, washed so the bars stay readable */}
        {lsl !== undefined && lsl > domainLo && (
          <rect
            x={PAD.left}
            y={PAD.top}
            width={Math.max(0, x(lsl) - PAD.left)}
            height={plotH}
            fill="var(--status-critical)"
            opacity={0.06}
          />
        )}
        {usl !== undefined && usl < domainHi && (
          <rect
            x={x(usl)}
            y={PAD.top}
            width={Math.max(0, width - PAD.right - x(usl))}
            height={plotH}
            fill="var(--status-critical)"
            opacity={0.06}
          />
        )}

        {bars.map((b) => {
          const w = Math.max(0.5, b.x1 - b.x0 - (b.x1 - b.x0 > 3 ? 1 : 0));
          const h = plotH - (y(b.c) - PAD.top);
          return (
            <rect
              key={b.i}
              x={b.x0}
              y={y(b.c)}
              width={w}
              height={Math.max(0, h)}
              fill={b.bad ? 'var(--status-critical)' : 'var(--series-1)'}
              opacity={hover === null || hover === b.i ? 1 : 0.55}
              onMouseEnter={() => setHover(b.i)}
            />
          );
        })}

        {/* Spec limits: drawn and labelled, so the colour wash is never the only cue */}
        {[
          { v: lsl, label: 'LSL' },
          { v: usl, label: 'USL' },
          { v: result.target, label: 'Target' },
        ]
          .filter((l): l is { v: number; label: string } => l.v !== undefined)
          .map((l) => (
            <g key={l.label}>
              <line
                x1={x(l.v)}
                x2={x(l.v)}
                y1={PAD.top - 6}
                y2={PAD.top + plotH}
                stroke={l.label === 'Target' ? 'var(--text-muted)' : 'var(--status-critical)'}
                strokeWidth={2}
              />
              <text
                x={x(l.v)}
                y={PAD.top - 10}
                textAnchor="middle"
                className="chart-label"
                fill="var(--text-secondary)"
              >
                {l.label}
              </text>
            </g>
          ))}

        {/* Axes */}
        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          stroke="var(--axis)"
          strokeWidth={1}
        />
        {ticks.map((t) => (
          <text
            key={t}
            x={x(t)}
            y={height - 12}
            textAnchor="middle"
            className="chart-tick"
            fill="var(--text-muted)"
          >
            {tickLabel(t)}
          </text>
        ))}
        {niceTicks(0, view.peak, 4).map((c) => (
          <text
            key={c}
            x={PAD.left - 8}
            y={y(c) + 4}
            textAnchor="end"
            className="chart-tick"
            fill="var(--text-muted)"
          >
            {c >= 1000 ? `${Math.round(c / 1000)}k` : c}
          </text>
        ))}

        {hovered && (
          <g pointerEvents="none">
            <rect
              x={Math.min(width - 170, Math.max(2, (hovered.x0 + hovered.x1) / 2 - 80))}
              y={PAD.top + 4}
              width={168}
              height={38}
              rx={4}
              fill="var(--surface-1)"
              stroke="var(--border-strong)"
            />
            <text
              x={Math.min(width - 162, Math.max(10, (hovered.x0 + hovered.x1) / 2 - 72))}
              y={PAD.top + 20}
              className="chart-tick"
              fill="var(--text-primary)"
            >
              {eng(histogram.lo + hovered.i * histogram.binWidth, 4, unit)}
            </text>
            <text
              x={Math.min(width - 162, Math.max(10, (hovered.x0 + hovered.x1) / 2 - 72))}
              y={PAD.top + 34}
              className="chart-tick"
              fill="var(--text-secondary)"
            >
              {count(hovered.c)} samples ({percent(hovered.c / stats.count, 2)})
            </text>
          </g>
        )}
      </svg>

      <p className="chart-note">
        {count(stats.count)} samples
        {outOfSpec > 0 ? (
          <>
            {' · '}
            <strong className="bad">{count(outOfSpec)} out of spec</strong>
            {stats.belowLsl > 0 && ` (${count(stats.belowLsl)} below LSL`}
            {stats.belowLsl > 0 && stats.aboveUsl > 0 && ', '}
            {stats.aboveUsl > 0 &&
              `${stats.belowLsl > 0 ? '' : '('}${count(stats.aboveUsl)} above USL`}
            {outOfSpec > 0 && ')'}
          </>
        ) : (
          (lsl !== undefined || usl !== undefined) && ' · none out of spec'
        )}
        {(histogram.underflow > 0 || histogram.overflow > 0) && (
          <>
            {' · '}
            <span className="muted">
              {count(histogram.underflow + histogram.overflow)} beyond the plotted range
              (counted exactly in the yield)
            </span>
          </>
        )}
      </p>

      {showTable && (
        <div className="table-scroll">
          <table className="data-table">
            <caption>Histogram bins for {result.id}</caption>
            <thead>
              <tr>
                <th scope="col">Bin lower</th>
                <th scope="col">Bin upper</th>
                <th scope="col">Count</th>
                <th scope="col">Share</th>
              </tr>
            </thead>
            <tbody>
              {bars
                .filter((b) => b.c > 0)
                .map((b) => (
                  <tr key={b.i}>
                    <td>{eng(histogram.lo + b.i * histogram.binWidth, 5, unit)}</td>
                    <td>{eng(histogram.lo + (b.i + 1) * histogram.binWidth, 5, unit)}</td>
                    <td>{count(b.c)}</td>
                    <td>{percent(b.c / stats.count, 3)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
